/**
 * AdminGuard Component
 * Restricts access to admin-only routes
 * Checks if user role === 'admin'
 */

import React from 'react';
import { Navigate } from 'react-router-dom';
import { Box, CircularProgress, Typography } from '@mui/material';
import { ShieldAlert } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

interface AdminGuardProps {
  children: React.ReactNode;
}

export const AdminGuard: React.FC<AdminGuardProps> = ({ children }) => {
  const { user, isLoading, isAuthenticated } = useAuth();

  // Show loading spinner while checking authentication
  if (isLoading) {
    return (
      <Box className="flex h-screen items-center justify-center bg-slate-50 dark:bg-slate-900">
        <CircularProgress />
      </Box>
    );
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated || !user) {
    return <Navigate to="/login" replace />;
  }

  // Check if user has admin role (case-insensitive)
  if (user.role.toLowerCase() !== 'admin') {
    return (
      <Box className="flex h-screen items-center justify-center bg-slate-50 dark:bg-slate-900">
        <Box className="text-center">
          <ShieldAlert size={64} className="mx-auto mb-4 text-red-500" />
          <Typography variant="h5" className="mb-2 font-bold text-gray-900 dark:text-slate-100">
            Access Denied
          </Typography>
          <Typography variant="body1" className="text-gray-600 dark:text-slate-400">
            You do not have permission to access the admin panel.
          </Typography>
          <Typography variant="body2" className="mt-4 text-gray-500 dark:text-slate-500">
            Required role: Admin | Your role: {user.role}
          </Typography>
        </Box>
      </Box>
    );
  }

  // User is admin, render children
  return <>{children}</>;
};
